import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { useNotification } from '../../contexts/NotificationContext';
import ProtectedRoute from '../../components/ProtectedRoute';
import AdminLayout, { useTheme } from '../../components/AdminLayout';
import Skeleton from '../../components/Skeleton';

function VIPCalendarContent() {
  const { t } = useTranslation();
  const { showAlert } = useNotification();
  const { T, S } = useTheme();

  const [bookings, setBookings] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [liveStats, setLiveStats] = useState({ en_route: 0, windows_open: 0, handoff_active: 0 });

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await api.getManagerPremiumBookings(statusFilter || undefined, selectedBranchId || undefined);
      setBookings(data || []);

      const active = await api.getManagerActiveVips();
      setLiveStats({
        en_route: (active || []).filter(v => v.status === 'en_route').length,
        windows_open: (active || []).filter(v => v.status === 'window_open').length,
        handoff_active: (active || []).filter(v => v.status === 'handoff_in_progress').length,
      });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    api.getManagerBranches().then(setBranches).catch(console.error);
  }, []);

  useEffect(() => { fetchData(); }, [statusFilter, selectedBranchId, selectedDate]);

  const handleInitiateHandoff = async (bookingId) => {
    try {
      await api.updateManagerPremiumBookingStatus(bookingId, 'handoff_in_progress');
      showAlert(t('vip.handoffInitiated', 'Handoff initiated'));
      fetchData();
    } catch (err) { showAlert(err.message); }
  };

  const statusConfig = {
    en_route: { color: '#22c55e', label: t('vip.enRoute', 'En Route'), bg: '#22c55e18' },
    window_open: { color: '#f59e0b', label: t('vip.windowOpen', 'Window Open'), bg: '#f59e0b18' },
    handoff_in_progress: { color: '#a855f7', label: t('vip.handoffActive', 'Handoff Active'), bg: '#a855f718' },
    in_service: { color: '#3b82f6', label: t('vip.inService', 'In Service'), bg: '#3b82f618' },
    completed: { color: '#6b7280', label: t('vip.completed', 'Completed'), bg: '#6b728018' },
  };

  return (
    <AdminLayout role="manager">
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={S.pageTitle}>{t('vip.calendarToday', 'VIP Calendar - Today')}</h1>
        <div style={{ width: '40px', height: '2px', background: T.orange }} />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="date"
          value={selectedDate}
          onChange={e => setSelectedDate(e.target.value)}
          style={{ ...S.input, maxWidth: '180px' }}
        />
        <select
          value={selectedBranchId}
          onChange={e => setSelectedBranchId(e.target.value)}
          style={{ ...S.input, maxWidth: '250px' }}
        >
          <option value="">{t('vip.allBranches', 'All Branches')}</option>
          {branches.map(b => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          style={{ ...S.input, maxWidth: '200px' }}
        >
          <option value="">{t('vip.allStatuses', 'All Statuses')}</option>
          {Object.entries(statusConfig).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
      </div>

      {/* Live KPIs */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
        {[
          { label: t('vip.enRoute', 'En Route'), value: liveStats.en_route, color: '#22c55e' },
          { label: t('vip.windowsOpen', 'Windows Open'), value: liveStats.windows_open, color: '#f59e0b' },
          { label: t('vip.handoffActive', 'Handoff Active'), value: liveStats.handoff_active, color: '#a855f7' },
        ].map(kpi => (
          <div key={kpi.label} style={{
            background: T.card, borderRadius: '12px', padding: '1rem 1.5rem',
            borderLeft: `4px solid ${kpi.color}`, flex: 1, minWidth: '180px',
          }}>
            <div style={{ fontSize: '0.72rem', color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
              {kpi.label}
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 700, color: kpi.color, fontFamily: "'DM Sans', sans-serif" }}>
              {kpi.value}
            </div>
          </div>
        ))}
      </div>

      {/* VIP Cards Grid */}
      {loading ? <Skeleton count={6} height="140px" /> : bookings.length === 0 ? (
        <p style={{ color: T.muted }}>{t('vip.noBookings', 'No VIP bookings found')}</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
          {bookings.map(b => {
            const cfg = statusConfig[b.status] || statusConfig.completed;
            return (
              <div key={b.id} style={{
                ...S.card,
                padding: '1.25rem',
                borderLeft: `4px solid ${cfg.color}`,
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
              }}>
                {/* Status Badge */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{
                    padding: '0.2rem 0.65rem', borderRadius: '20px',
                    fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase',
                    background: cfg.bg, color: cfg.color,
                  }}>
                    {cfg.label}
                  </span>
                  <span style={{ fontSize: '0.78rem', color: T.muted }}>
                    {b.booking_time || ''} {b.end_time ? `- ${b.end_time}` : ''}
                  </span>
                </div>

                {/* Client Info */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                  <div style={{
                    width: '36px', height: '36px', borderRadius: '50%',
                    background: `linear-gradient(135deg, ${cfg.color}, ${cfg.color}88)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontWeight: 700, fontSize: '0.82rem', flexShrink: 0,
                  }}>
                    {(b.client_name || '?')[0].toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: T.white, fontSize: '0.9rem' }}>{b.client_name}</div>
                    <div style={{ fontSize: '0.72rem', color: T.muted }}>
                      {b.artist_name && <>{t('vip.with', 'with')} {b.artist_name}</>}
                      {b.branch_name && <> &middot; {b.branch_name}</>}
                    </div>
                  </div>
                </div>

                {/* Handoff Button for EN ROUTE */}
                {b.status === 'en_route' && (
                  <button
                    onClick={() => handleInitiateHandoff(b.id)}
                    style={{
                      padding: '0.5rem 1rem', borderRadius: '8px',
                      border: '2px solid #a855f7', background: 'transparent',
                      color: '#a855f7', cursor: 'pointer', fontWeight: 600,
                      fontSize: '0.8rem', textTransform: 'uppercase',
                      letterSpacing: '0.04em', transition: 'all 0.2s',
                    }}
                    onMouseEnter={e => { e.target.style.background = '#a855f718'; }}
                    onMouseLeave={e => { e.target.style.background = 'transparent'; }}
                  >
                    {t('vip.initiateHandoff', 'Initiate Handoff')}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
}

export default function VIPCalendar() {
  return <ProtectedRoute requireSalonManager><VIPCalendarContent /></ProtectedRoute>;
}
